import type { CloudUser, SessionResponse, UsageSummary } from '@shared/api';
import { ApiClient, CloudError } from './ApiClient';

/**
 * Who is signed in, and what this deployment can do.
 *
 * The app boots by calling {@link CloudSession.refresh} exactly once, and everything downstream —
 * which project store to use, whether to show a sign-in button, whether AI is available — reads the
 * answer from here rather than probing the server again.
 *
 * The important design point is {@link CloudSession.mode}. A deployment with no API at all (the
 * original `npm run dev` against a static Vite server, or a `file://` open of `dist/`) must keep
 * working exactly as it did: sites in IndexedDB, key in `localStorage`. So a failed or 404 session
 * probe is not an error — it resolves to `'local'` and the app is unchanged. Signing in moves the
 * mode to `'cloud'`; signing out moves it back.
 */

export type StorageMode = 'local' | 'cloud';

export interface SessionState {
  mode: StorageMode;
  user: CloudUser | null;
  /** Whether this deployment offers Google sign-in. False on a local-only build. */
  authAvailable: boolean;
  /**
   * Whether the deployment refuses anonymous use.
   *
   * When true and nobody is signed in, the app must not boot the editor at all — see
   * {@link CloudSession.locked}.
   */
  requireAuth: boolean;
  /** Whether AI works without the user supplying their own key. */
  platformAiAvailable: boolean;
  /**
   * Whether this account may open the operator console.
   *
   * Reported so the account menu can offer a link rather than leaving `/admin` to be typed from
   * memory. It is emphatically not the access control — the server checks `ADMIN_EMAILS` on every
   * admin route and answers 404 to anyone else — so a client that set this itself would win a menu
   * item and a page full of refusals.
   */
  isAdmin: boolean;
  usage: UsageSummary | null;
  /** True once {@link CloudSession.refresh} has resolved, however it resolved. */
  ready: boolean;
}

const ANONYMOUS: SessionState = {
  mode: 'local',
  user: null,
  authAvailable: false,
  requireAuth: false,
  platformAiAvailable: false,
  isAdmin: false,
  usage: null,
  ready: false,
};

export class CloudSession {
  private state: SessionState = { ...ANONYMOUS };
  private readonly listeners = new Set<(state: SessionState) => void>();
  private inFlight: Promise<SessionState> | null = null;

  constructor(readonly api: ApiClient = new ApiClient()) {}

  get current(): SessionState {
    return this.state;
  }

  get signedIn(): boolean {
    return this.state.user !== null;
  }

  get mode(): StorageMode {
    return this.state.mode;
  }

  /**
   * Whether the app must refuse to open at all.
   *
   * Only ever true on a deployment that set `REQUIRE_AUTH` *and* configured OAuth — the server
   * refuses to start on the half-configured combination, so this cannot become an unopenable door.
   */
  get locked(): boolean {
    return this.state.requireAuth && this.state.user === null;
  }

  onChange(listener: (state: SessionState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Ask the server who we are. Memoised while in flight so a dozen callers cost one request.
   *
   * Never rejects. A deployment without the API is a supported configuration, not a failure, and
   * a boot path that can throw here would take the whole app down when the backend is briefly
   * unreachable — for a tool whose local mode works fine offline, that would be absurd.
   */
  async refresh(): Promise<SessionState> {
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.probe().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async probe(): Promise<SessionState> {
    try {
      const response = await this.api.getJson<SessionResponse>('/api/session');
      this.set({
        mode: response.user ? 'cloud' : 'local',
        user: response.user,
        authAvailable: response.authAvailable,
        requireAuth: response.requireAuth,
        platformAiAvailable: response.platformAiAvailable,
        isAdmin: response.isAdmin,
        usage: response.usage ?? null,
        ready: true,
      });
    } catch (error) {
      if (!(error instanceof CloudError)) throw error;
      this.set({ ...ANONYMOUS, ready: true });
    }
    return this.state;
  }

  /**
   * Begin Google sign-in.
   *
   * A full-page navigation, not a popup or a fetch: the handshake ends in a redirect that sets an
   * httpOnly cookie, and neither a popup nor an XHR can be the thing that navigation lands on
   * without extra machinery. `returnTo` brings the user back to the page they were on — the server
   * constrains it to a same-origin path.
   */
  signIn(returnTo: string = globalThis.location?.pathname ?? '/'): void {
    const url = `/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
    globalThis.location?.assign(this.api.url(url));
  }

  /** Drop the session on the server and fall back to local storage without a reload. */
  async signOut(everywhere = false): Promise<void> {
    try {
      await this.api.postJson(everywhere ? '/auth/logout-all' : '/auth/logout', {});
    } catch (error) {
      // The cookie may already be gone. Local state is what the UI reads, so clear it regardless.
      if (!(error instanceof CloudError)) throw error;
    }
    // Admin follows the account, not the browser: the menu item must go the moment the account does.
    this.set({ ...this.state, mode: 'local', user: null, isAdmin: false, usage: null });
  }

  /** Store or clear the user's own OpenAI key. Empty string forgets it. */
  async setOwnApiKey(apiKey: string): Promise<UsageSummary> {
    const { usage } = await this.api.putJson<{ usage: UsageSummary }>(
      '/api/settings/openai-key',
      { apiKey },
    );
    this.set({ ...this.state, usage });
    return usage;
  }

  /** Re-read the usage counters — called after a render so the allowance display stays honest. */
  async refreshUsage(): Promise<void> {
    if (!this.signedIn) return;
    try {
      const response = await this.api.getJson<SessionResponse>('/api/session');
      this.set({ ...this.state, usage: response.usage ?? null });
    } catch (error) {
      if (!(error instanceof CloudError)) throw error;
      // A stale counter is not worth interrupting anyone over.
    }
  }

  /**
   * Note that the session expired, without a network round trip.
   *
   * Any cloud call that comes back `unauthenticated` means the cookie died — expired, revoked, or
   * cleared in another tab. Flipping to local mode here keeps the app usable instead of leaving
   * every subsequent save failing against a store that can no longer authenticate.
   */
  markSignedOut(): void {
    if (this.state.user === null) return;
    this.set({ ...this.state, mode: 'local', user: null, isAdmin: false, usage: null });
  }

  private set(state: SessionState): void {
    this.state = state;
    for (const listener of this.listeners) listener(state);
  }
}
